import React, { useState, useEffect, useCallback } from "react";
import { financialsAPI, clientsAPI, exportAPI, savingsAPI, incomeAllocationsAPI } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calculator,
  FileText,
  Plus,
  Upload,
  Download,
  Trash2,
  Edit,
  Receipt,
  Package,
  Settings,
  Search,
  Filter,
  X,
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
} from "lucide-react";

const PAYMENT_METHODS = ["PayPal", "Bank", "Cash", "GCash", "Maya", "Other"];
// Floating-point tolerance for allocation amount comparisons (half a cent)
const AMOUNT_TOLERANCE = 0.005;
const EXPENSE_CATEGORIES = [
  "Utilities",
  "Internet",
  "Equipment",
  "Software",
  "Marketing",
  "Office Supplies",
  "Transportation",
  "Meals",
  "Other",
];

const formatCurrency = (amount, currency = "PHP") => {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: currency,
  }).format(amount || 0);
};

// ============ INCOME TAB ============
const IncomeTab = ({ clients }) => {
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  // Split allocation state
  const [splitMode, setSplitMode] = useState(false);
  const [allocationRows, setAllocationRows] = useState([]);
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [formData, setFormData] = useState({
    date_received: new Date().toISOString().split("T")[0],
    description: "",
    gross_amount: "",
    currency: "PHP",
    payment_method: "Bank",
    official_receipt_no: "",
    client_id: "",
    notes: "",
    status: "confirmed",
    allocated_month: "",
  });

  const fetchIncome = useCallback(async () => {
    try {
      const params = {};
      if (filterMethod) params.payment_method = filterMethod;
      const res = await financialsAPI.getIncome(params);
      setIncome(res.data);
    } catch (err) {
      toast.error("Failed to load income records");
    } finally {
      setLoading(false);
    }
  }, [filterMethod]);

  useEffect(() => {
    fetchIncome();
  }, [fetchIncome]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const grossAmt = parseFloat(formData.gross_amount);

    // Validate split allocations
    if (splitMode && allocationRows.length > 0) {
      const totalAllocated = allocationRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
      if (totalAllocated > grossAmt + AMOUNT_TOLERANCE) {
        toast.error("Total allocated amount exceeds gross amount");
        return;
      }
      for (const row of allocationRows) {
        if (!row.allocation_month || !/^\d{4}-\d{2}$/.test(row.allocation_month)) {
          toast.error("Each allocation row must have a valid month (YYYY-MM)");
          return;
        }
        if (!row.amount || parseFloat(row.amount) <= 0) {
          toast.error("Each allocation row must have a positive amount");
          return;
        }
      }
    }

    try {
      const data = {
        ...formData,
        gross_amount: grossAmt,
        client_id: formData.client_id || null,
        allocated_month: splitMode ? null : (formData.allocated_month || null),
        status: formData.status || "confirmed",
      };

      let savedId;
      if (editingItem) {
        await financialsAPI.updateIncome(editingItem.id, data);
        savedId = editingItem.id;
        toast.success("Income updated successfully");
      } else {
        const res = await financialsAPI.createIncome(data);
        savedId = res.data.id;
        toast.success("Income recorded successfully");
      }

      // Handle split allocations
      if (splitMode && savedId) {
        // Delete removed existing allocations
        const newIds = allocationRows.filter((r) => r.id).map((r) => r.id);
        for (const ea of existingAllocations) {
          if (!newIds.includes(ea.id)) {
            // Ignore errors for individual deletions to avoid blocking the save
            await incomeAllocationsAPI.delete(ea.id).catch(() => {});
          }
        }
        // Upsert rows
        for (const row of allocationRows) {
          const payload = {
            income_id: savedId,
            allocation_month: row.allocation_month,
            amount: parseFloat(row.amount),
          };
          if (row.id) {
            await incomeAllocationsAPI.update(row.id, { allocation_month: row.allocation_month, amount: parseFloat(row.amount) });
          } else {
            await incomeAllocationsAPI.create(payload);
          }
        }
      } else if (!splitMode && editingItem) {
        // If switching from split to simple, delete all existing allocations
        for (const ea of existingAllocations) {
          // Ignore errors for individual deletions to avoid blocking the save
          await incomeAllocationsAPI.delete(ea.id).catch(() => {});
        }
      }

      setShowDialog(false);
      setEditingItem(null);
      resetForm();
      fetchIncome();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save income");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this income record?")) return;
    try {
      await financialsAPI.deleteIncome(id);
      toast.success("Income deleted");
      fetchIncome();
    } catch (err) {
      toast.error("Failed to delete income");
    }
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({
      date_received: item.date_received,
      description: item.description,
      gross_amount: item.gross_amount.toString(),
      currency: item.currency,
      payment_method: item.payment_method,
      official_receipt_no: item.official_receipt_no || "",
      client_id: item.client_id || "",
      notes: item.notes || "",
      status: item.status || "confirmed",
      allocated_month: item.allocated_month || "",
    });
    // Fetch existing allocations
    try {
      const res = await incomeAllocationsAPI.list({ income_id: item.id });
      const allocations = res.data || [];
      setExistingAllocations(allocations);
      if (allocations.length > 0) {
        setSplitMode(true);
        setAllocationRows(allocations.map((a) => ({ id: a.id, allocation_month: a.allocation_month, amount: a.amount.toString() })));
      } else {
        setSplitMode(false);
        setAllocationRows([]);
      }
    } catch {
      setExistingAllocations([]);
      setSplitMode(false);
      setAllocationRows([]);
    }
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      date_received: new Date().toISOString().split("T")[0],
      description: "",
      gross_amount: "",
      currency: "PHP",
      payment_method: "Bank",
      official_receipt_no: "",
      client_id: "",
      notes: "",
      status: "confirmed",
      allocated_month: "",
    });
    setSplitMode(false);
    setAllocationRows([]);
    setExistingAllocations([]);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await financialsAPI.importIncome(file);
      toast.success(`Imported ${res.data.imported} income records`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      setShowImportDialog(false);
      fetchIncome();
    } catch (err) {
      toast.error("Failed to import income");
    }
  };

  const filteredIncome = income.filter(
    (i) =>
      i.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.official_receipt_no?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalIncome = filteredIncome.reduce((sum, i) => sum + (i.gross_amount || 0), 0);

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search income..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full sm:w-64"
              data-testid="income-search"
            />
          </div>
          <Select value={filterMethod || "all"} onValueChange={(v) => setFilterMethod(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-40" data-testid="income-filter-method">
              <SelectValue placeholder="All Methods" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              {PAYMENT_METHODS.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)} data-testid="import-income-btn">
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <a href={exportAPI.financialIncome()} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" data-testid="export-income-btn">
              <Download className="h-4 w-4 mr-2" /> Export
            </Button>
          </a>
          <Button size="sm" onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-income-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Income
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-muted-foreground text-sm">Total Income ({filteredIncome.length} records)</span>
            <span className="text-xl sm:text-2xl font-bold text-green-600">{formatCurrency(totalIncome)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Table – desktop */}
      <Card className="hidden sm:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Allocated</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIncome.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No income records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIncome.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.date_received}</TableCell>
                      <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                      <TableCell>{clients.find(c => c.id === item.client_id)?.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.payment_method}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.status === "expected" ? "secondary" : "default"} className="text-xs">
                          {item.status === "expected" ? "Expected" : "Confirmed"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {item.allocated_month || "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium text-green-600">
                        {formatCurrency(item.gross_amount, item.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {filteredIncome.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">No income records found</CardContent></Card>
        ) : (
          filteredIncome.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{item.description}</p>
                    <p className="text-xs text-muted-foreground">{item.date_received} · {clients.find(c => c.id === item.client_id)?.name || "No client"}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">{item.payment_method}</Badge>
                      <Badge variant={item.status === "expected" ? "secondary" : "default"} className="text-xs">
                        {item.status === "expected" ? "Expected" : "Confirmed"}
                      </Badge>
                      {item.allocated_month && (
                        <span className="text-xs text-muted-foreground">{item.allocated_month}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="font-bold text-green-600 text-sm">{formatCurrency(item.gross_amount, item.currency)}</span>
                    <div className="flex">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(item)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Income" : "Add Income"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Date Received *</Label>
                <Input
                  type="date"
                  value={formData.date_received}
                  onChange={(e) => setFormData({ ...formData, date_received: e.target.value })}
                  required
                  data-testid="income-date"
                />
              </div>
              <div>
                <Label>Payment Method *</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger data-testid="income-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What is this income for?"
                required
                data-testid="income-description"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Gross Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.gross_amount}
                  onChange={(e) => setFormData({ ...formData, gross_amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="income-amount"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PHP">PHP</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="income-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="expected">Expected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client (Optional)</Label>
                <Select value={formData.client_id || "none"} onValueChange={(v) => setFormData({ ...formData, client_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="income-client">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Client</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Official Receipt #</Label>
                <Input
                  value={formData.official_receipt_no}
                  onChange={(e) => setFormData({ ...formData, official_receipt_no: e.target.value })}
                  placeholder="OR-0001"
                  data-testid="income-or"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Allocation Section */}
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Allocation</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!splitMode) {
                      setSplitMode(true);
                      // Pre-populate with simple mode value if set
                      if (formData.allocated_month && allocationRows.length === 0) {
                        const grossAmt = parseFloat(formData.gross_amount) || 0;
                        setAllocationRows([{ id: null, allocation_month: formData.allocated_month, amount: grossAmt.toString() }]);
                        setFormData((f) => ({ ...f, allocated_month: "" }));
                      } else if (allocationRows.length === 0) {
                        setAllocationRows([{ id: null, allocation_month: "", amount: "" }]);
                      }
                    } else {
                      setSplitMode(false);
                      setAllocationRows([]);
                    }
                  }}
                  data-testid="toggle-split-btn"
                >
                  {splitMode ? "Simple Mode" : "Split across months"}
                </Button>
              </div>

              {!splitMode && (
                <div>
                  <Label className="text-xs text-muted-foreground">Allocation Month (YYYY-MM)</Label>
                  <Input
                    type="month"
                    value={formData.allocated_month}
                    onChange={(e) => setFormData({ ...formData, allocated_month: e.target.value })}
                    placeholder="YYYY-MM"
                    data-testid="income-allocated-month"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Earmark this income to a specific month for projected coverage.</p>
                </div>
              )}

              {splitMode && (
                <div className="space-y-2">
                  {(() => {
                    const grossAmt = parseFloat(formData.gross_amount) || 0;
                    const totalAllocated = allocationRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
                    const remaining = grossAmt - totalAllocated;
                    return (
                      <>
                        {allocationRows.map((row, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <Input
                              type="month"
                              value={row.allocation_month}
                              onChange={(e) => {
                                const updated = [...allocationRows];
                                updated[idx] = { ...updated[idx], allocation_month: e.target.value };
                                setAllocationRows(updated);
                              }}
                              className="flex-1"
                              data-testid={`alloc-month-${idx}`}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={row.amount}
                              onChange={(e) => {
                                const updated = [...allocationRows];
                                updated[idx] = { ...updated[idx], amount: e.target.value };
                                setAllocationRows(updated);
                              }}
                              placeholder="Amount"
                              className="w-28"
                              data-testid={`alloc-amount-${idx}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 flex-shrink-0"
                              onClick={() => setAllocationRows(allocationRows.filter((_, i) => i !== idx))}
                              data-testid={`alloc-delete-${idx}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setAllocationRows([...allocationRows, { id: null, allocation_month: "", amount: "" }])}
                          data-testid="add-alloc-row-btn"
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add Row
                        </Button>
                        <div className={`text-sm font-medium ${remaining < -AMOUNT_TOLERANCE ? "text-red-600" : "text-muted-foreground"}`}>
                          Allocated: {formatCurrency(totalAllocated, formData.currency)} / {formatCurrency(grossAmt, formData.currency)}
                          {remaining >= AMOUNT_TOLERANCE && (
                            <span className="ml-2 text-amber-600">({formatCurrency(remaining, formData.currency)} unallocated)</span>
                          )}
                          {remaining < -AMOUNT_TOLERANCE && (
                            <span className="ml-2 text-red-600">(exceeds gross amount)</span>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-income-btn">Save Income</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Income from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with columns: date_received, description, gross_amount, currency, payment_method, official_receipt_no, notes
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={handleImport}
              data-testid="income-csv-input"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ EXPENSES TAB ============
const ExpensesTab = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [balancePreview, setBalancePreview] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    vendor_name: "",
    category: "Other",
    description: "",
    amount: "",
    currency: "PHP",
    payment_method: "Bank",
    deductible: true,
    notes: "",
    paid_from_account_id: "",
    user_confirmed_balance_after: "",
  });

  const fetchExpenses = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      const [expensesRes, accountsRes] = await Promise.all([
        financialsAPI.getExpenses(params),
        savingsAPI.list().catch(() => ({ data: [] })),
      ]);
      setExpenses(expensesRes.data);
      setAccounts(accountsRes.data || []);
    } catch (err) {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        paid_from_account_id: formData.paid_from_account_id || undefined,
        user_confirmed_balance_after: formData.user_confirmed_balance_after !== "" ? parseFloat(formData.user_confirmed_balance_after) : undefined,
      };

      if (editingItem) {
        await financialsAPI.updateExpense(editingItem.id, data);
        toast.success("Expense updated successfully");
      } else {
        await financialsAPI.createExpense(data);
        toast.success("Expense recorded successfully");
      }

      setShowDialog(false);
      setEditingItem(null);
      setBalancePreview(null);
      resetForm();
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save expense");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) return;
    try {
      await financialsAPI.deleteExpense(id);
      toast.success("Expense deleted");
      fetchExpenses();
    } catch (err) {
      toast.error("Failed to delete expense");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setBalancePreview(null);
    setFormData({
      date: item.date,
      vendor_name: item.vendor_name,
      category: item.category,
      description: item.description,
      amount: item.amount.toString(),
      currency: item.currency,
      payment_method: item.payment_method,
      deductible: item.deductible,
      notes: item.notes || "",
      paid_from_account_id: item.paid_from_account_id || "",
      user_confirmed_balance_after: "",
    });
    setShowDialog(true);
  };

  const handleReceiptUpload = async (expenseId, file) => {
    try {
      await financialsAPI.uploadReceipt(expenseId, file);
      toast.success("Receipt uploaded");
      fetchExpenses();
    } catch (err) {
      toast.error("Failed to upload receipt");
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      vendor_name: "",
      category: "Other",
      description: "",
      amount: "",
      currency: "PHP",
      payment_method: "Bank",
      deductible: true,
      notes: "",
      paid_from_account_id: "",
      user_confirmed_balance_after: "",
    });
    setBalancePreview(null);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await financialsAPI.importExpenses(file);
      toast.success(`Imported ${res.data.imported} expense records`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      setShowImportDialog(false);
      fetchExpenses();
    } catch (err) {
      toast.error("Failed to import expenses");
    }
  };

  const filteredExpenses = expenses.filter(
    (e) =>
      e.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const deductibleTotal = filteredExpenses.filter(e => e.deductible).reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full sm:w-64"
              data-testid="expense-search"
            />
          </div>
          <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-40" data-testid="expense-filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {EXPENSE_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)} data-testid="import-expense-btn">
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <a href={exportAPI.financialExpenses()} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" data-testid="export-expense-btn">
              <Download className="h-4 w-4 mr-2" /> Export
            </Button>
          </a>
          <Button size="sm" onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-expense-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="overflow-hidden">
          <CardContent className="pt-4">
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs">Total Expenses</span>
              <span className="text-lg sm:text-2xl font-bold text-red-600 truncate">{formatCurrency(totalExpenses)}</span>
            </div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="pt-4">
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-xs">Deductible</span>
              <span className="text-lg sm:text-2xl font-bold text-blue-600 truncate">{formatCurrency(deductibleTotal)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table – desktop */}
      <Card className="hidden sm:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Deductible</TableHead>
                  <TableHead>Receipt</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No expenses found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredExpenses.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.date}</TableCell>
                      <TableCell>{item.vendor_name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{item.category}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                      <TableCell>
                        {item.deductible ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell>
                        {item.receipt_url ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${item.receipt_url}`} target="_blank" rel="noopener noreferrer">
                            <Receipt className="h-4 w-4 text-blue-600" />
                          </a>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleReceiptUpload(item.id, e.target.files[0]);
                                }
                              }}
                            />
                            <Upload className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                          </label>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium text-red-600">
                        {formatCurrency(item.amount, item.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {filteredExpenses.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">No expenses found</CardContent></Card>
        ) : (
          filteredExpenses.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{item.vendor_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                    <p className="text-xs text-muted-foreground">{item.date}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                      {item.deductible && <Badge variant="outline" className="text-xs text-green-600 border-green-600/30">Deductible</Badge>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="font-bold text-red-600 text-sm">{formatCurrency(item.amount, item.currency)}</span>
                    <div className="flex">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(item)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Expense" : "Add Expense"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="expense-date"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="expense-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Vendor Name *</Label>
              <Input
                value={formData.vendor_name}
                onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                placeholder="Who did you pay?"
                required
                data-testid="expense-vendor"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What is this expense for?"
                required
                data-testid="expense-description"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="expense-amount"
                />
              </div>
              <div>
                <Label>Payment Method *</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger data-testid="expense-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.deductible}
                onCheckedChange={(v) => setFormData({ ...formData, deductible: v })}
                id="deductible"
                data-testid="expense-deductible"
              />
              <Label htmlFor="deductible">Tax Deductible</Label>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            {/* Paid From Account (optional) */}
            {accounts.length > 0 && (
              <div className="space-y-2 border-t border-border pt-4">
                <Label>Paid From Account (optional)</Label>
                <Select
                  value={formData.paid_from_account_id || "none"}
                  onValueChange={async (val) => {
                    const accountId = val === "none" ? "" : val;
                    setFormData((f) => ({ ...f, paid_from_account_id: accountId, user_confirmed_balance_after: "" }));
                    setBalancePreview(null);
                    if (accountId) {
                      const amt = parseFloat(formData.amount) || 0;
                      if (amt > 0) {
                        try {
                          const previewRes = await savingsAPI.getBalancePreview(accountId, "outflow", amt);
                          setBalancePreview(previewRes.data);
                        } catch { /* ignore */ }
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No account update</SelectItem>
                    {accounts.map((a) => {
                      const bal = a.current_balance ?? a.amount ?? 0;
                      const label = `${a.account_label || a.bank_name} (${bal.toLocaleString()} ${a.currency})`;
                      return (
                        <SelectItem key={a.id} value={a.id}>{label}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>

                {balancePreview && (
                  <div className="rounded-md bg-muted/40 p-3 text-sm space-y-1 border border-border">
                    <p className="text-muted-foreground">
                      Current balance: <strong className="text-foreground">{balancePreview.current_balance?.toLocaleString()} {balancePreview.currency}</strong>
                    </p>
                    <p className="text-muted-foreground">
                      Balance after expense: <strong className={balancePreview.suggested_balance < 0 ? "text-red-500" : "text-foreground"}>{balancePreview.suggested_balance?.toLocaleString()} {balancePreview.currency}</strong>
                    </p>
                  </div>
                )}

                {formData.paid_from_account_id && balancePreview && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Confirm actual balance after (optional)</Label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder={String(balancePreview.suggested_balance)}
                      value={formData.user_confirmed_balance_after}
                      onChange={(e) => setFormData((f) => ({ ...f, user_confirmed_balance_after: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-expense-btn">Save Expense</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Expenses from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with columns: date, vendor_name, category, description, amount, payment_method, deductible, notes
            </p>
            <Input
              type="file"
              accept=".csv"
              onChange={handleImport}
              data-testid="expense-csv-input"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ ASSETS TAB ============
const AssetsTab = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    asset_name: "",
    purchase_date: new Date().toISOString().split("T")[0],
    purchase_amount: "",
    currency: "PHP",
    depreciation_years: "5",
    notes: "",
  });

  const fetchAssets = async () => {
    try {
      const res = await financialsAPI.getAssets();
      setAssets(res.data);
    } catch (err) {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        purchase_amount: parseFloat(formData.purchase_amount),
        depreciation_years: parseInt(formData.depreciation_years),
      };

      if (editingItem) {
        await financialsAPI.updateAsset(editingItem.id, data);
        toast.success("Asset updated successfully");
      } else {
        await financialsAPI.createAsset(data);
        toast.success("Asset added successfully");
      }

      setShowDialog(false);
      setEditingItem(null);
      resetForm();
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save asset");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this asset?")) return;
    try {
      await financialsAPI.deleteAsset(id);
      toast.success("Asset deleted");
      fetchAssets();
    } catch (err) {
      toast.error("Failed to delete asset");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      asset_name: item.asset_name,
      purchase_date: item.purchase_date,
      purchase_amount: item.purchase_amount.toString(),
      currency: item.currency,
      depreciation_years: item.depreciation_years.toString(),
      notes: item.notes || "",
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      asset_name: "",
      purchase_date: new Date().toISOString().split("T")[0],
      purchase_amount: "",
      currency: "PHP",
      depreciation_years: "5",
      notes: "",
    });
  };

  const totalValue = assets.reduce((sum, a) => sum + (a.purchase_amount || 0), 0);
  const currentValue = assets.reduce((sum, a) => sum + (a.current_value || 0), 0);
  const totalDepreciation = assets.reduce((sum, a) => sum + (a.total_depreciation || 0), 0);

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <h3 className="text-lg font-medium">Business Assets</h3>
        <div className="flex flex-wrap gap-2">
          <a href={exportAPI.financialAssets()} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" data-testid="export-assets-btn">
              <Download className="h-4 w-4 mr-2" /> Export
            </Button>
          </a>
          <Button size="sm" onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-asset-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Asset
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="overflow-hidden">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground mb-1">Original Value</div>
            <div className="text-base sm:text-2xl font-bold truncate">{formatCurrency(totalValue)}</div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground mb-1">Current Value</div>
            <div className="text-base sm:text-2xl font-bold text-green-600 truncate">{formatCurrency(currentValue)}</div>
          </CardContent>
        </Card>
        <Card className="overflow-hidden">
          <CardContent className="pt-4">
            <div className="text-xs text-muted-foreground mb-1">Depreciation</div>
            <div className="text-base sm:text-2xl font-bold text-red-600 truncate">{formatCurrency(totalDepreciation)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset Name</TableHead>
                  <TableHead>Purchase Date</TableHead>
                  <TableHead className="text-right">Original Value</TableHead>
                  <TableHead>Depreciation (yrs)</TableHead>
                  <TableHead className="text-right">Current Value</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No assets recorded
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.asset_name}</TableCell>
                      <TableCell>{item.purchase_date}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.purchase_amount, item.currency)}</TableCell>
                      <TableCell>{item.depreciation_years} years</TableCell>
                      <TableCell className="text-right text-green-600">{formatCurrency(item.current_value, item.currency)}</TableCell>
                      <TableCell className="max-w-xs truncate">{item.notes || "-"}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] sm:w-full">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Asset" : "Add Asset"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Asset Name *</Label>
              <Input
                value={formData.asset_name}
                onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
                placeholder="e.g., MacBook Pro, Office Chair"
                required
                data-testid="asset-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Purchase Date *</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  required
                  data-testid="asset-date"
                />
              </div>
              <div>
                <Label>Depreciation (years)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.depreciation_years}
                  onChange={(e) => setFormData({ ...formData, depreciation_years: e.target.value })}
                  data-testid="asset-depreciation"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Purchase Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.purchase_amount}
                  onChange={(e) => setFormData({ ...formData, purchase_amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="asset-amount"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PHP">PHP</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-asset-btn">Save Asset</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ TAX SUMMARY TAB ============
const TaxSummaryTab = () => {
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("monthly");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { period, year };
      if (period === "monthly") params.month = month;
      if (period === "quarterly") params.quarter = quarter;

      const [summaryRes, settingsRes] = await Promise.all([
        financialsAPI.getTaxSummary(params),
        financialsAPI.getTaxSettings(),
      ]);

      setSummary(summaryRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      toast.error("Failed to load tax summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, year, month, quarter]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      await financialsAPI.updateTaxSettings(settings);
      toast.success("Tax settings saved");
      setShowSettingsDialog(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];
  const quarters = [
    { value: 1, label: "Q1 (Jan-Mar)" },
    { value: 2, label: "Q2 (Apr-Jun)" },
    { value: 3, label: "Q3 (Jul-Sep)" },
    { value: 4, label: "Q4 (Oct-Dec)" },
  ];

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32" data-testid="tax-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24" data-testid="tax-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {period === "monthly" && (
            <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-36" data-testid="tax-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {period === "quarterly" && (
            <Select value={quarter.toString()} onValueChange={(v) => setQuarter(parseInt(v))}>
              <SelectTrigger className="w-36" data-testid="tax-quarter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {quarters.map((q) => (
                  <SelectItem key={q.value} value={q.value.toString()}>{q.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button variant="outline" onClick={() => setShowSettingsDialog(true)} data-testid="tax-settings-btn">
          <Settings className="h-4 w-4 mr-2" /> Tax Settings
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="overflow-hidden">
              <CardContent className="pt-4">
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                  <TrendingUp className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">Gross Income</span>
                </div>
                <div className="text-base sm:text-2xl font-bold text-green-600 truncate">
                  {formatCurrency(summary.total_gross_income)}
                </div>
                <div className="text-xs text-muted-foreground">{summary.income_count} records</div>
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="pt-4">
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                  <TrendingDown className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">Total Expenses</span>
                </div>
                <div className="text-base sm:text-2xl font-bold text-red-600 truncate">
                  {formatCurrency(summary.total_expenses)}
                </div>
                <div className="text-xs text-muted-foreground">{summary.expense_count} records</div>
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="pt-4">
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                  <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">Deductible</span>
                </div>
                <div className="text-base sm:text-2xl font-bold text-blue-600 truncate">
                  {formatCurrency(summary.total_deductible)}
                </div>
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="pt-4">
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                  <DollarSign className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">Net Income</span>
                </div>
                <div className="text-base sm:text-2xl font-bold truncate">
                  {formatCurrency(summary.net_income)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tax Calculation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg flex-wrap">
                <Calculator className="h-5 w-5" />
                Tax Calculation
                {summary.has_override && (
                  <Badge variant="outline" className="ml-2">Accountant Override</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 sm:p-4 bg-muted rounded-lg">
                    <div className="text-xs sm:text-sm text-muted-foreground">Tax Rate</div>
                    <div className="text-lg sm:text-xl font-bold">{summary.tax_percentage}%</div>
                  </div>
                  <div className="p-3 sm:p-4 bg-muted rounded-lg">
                    <div className="text-xs sm:text-sm text-muted-foreground">Estimated Tax</div>
                    <div className="text-base sm:text-xl font-bold text-orange-600 truncate">
                      {formatCurrency(summary.estimated_tax)}
                    </div>
                  </div>
                  <div className="p-3 sm:p-4 bg-muted rounded-lg">
                    <div className="text-xs sm:text-sm text-muted-foreground">Withholding Tax</div>
                    <div className="text-base sm:text-xl font-bold text-blue-600 truncate">
                      {formatCurrency(summary.withholding_tax)}
                    </div>
                  </div>
                  {summary.vat_enabled && (
                    <div className="p-3 sm:p-4 bg-muted rounded-lg">
                      <div className="text-xs sm:text-sm text-muted-foreground">VAT Amount</div>
                      <div className="text-base sm:text-xl font-bold text-purple-600 truncate">
                        {formatCurrency(summary.vat_amount)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-6 bg-primary/10 rounded-lg">
                  <div className="text-sm text-muted-foreground">Net Tax Payable</div>
                  <div className="text-2xl sm:text-3xl font-bold text-primary truncate">
                    {formatCurrency(summary.net_payable)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    = Estimated Tax ({formatCurrency(summary.estimated_tax)}) - Withholding ({formatCurrency(summary.withholding_tax)})
                    {summary.vat_enabled && ` + VAT (${formatCurrency(summary.vat_amount)})`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Income by Method & Expenses by Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Income by Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(summary.income_by_method || {}).length === 0 ? (
                  <p className="text-muted-foreground text-sm">No income data</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(summary.income_by_method).map(([method, amount]) => (
                      <div key={method} className="flex justify-between gap-2">
                        <span className="text-sm truncate">{method}</span>
                        <span className="font-medium text-green-600 flex-shrink-0">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Expenses by Category</CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(summary.expenses_by_category || {}).length === 0 ? (
                  <p className="text-muted-foreground text-sm">No expense data</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(summary.expenses_by_category).map(([category, amount]) => (
                      <div key={category} className="flex justify-between gap-2">
                        <span className="text-sm truncate">{category}</span>
                        <span className="font-medium text-red-600 flex-shrink-0">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Tax Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tax Settings</DialogTitle>
          </DialogHeader>
          {settings && (
            <form onSubmit={handleSaveSettings} className="space-y-4">
              <div>
                <Label>Tax Percentage (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={settings.tax_percentage}
                  onChange={(e) => setSettings({ ...settings, tax_percentage: parseFloat(e.target.value) })}
                  data-testid="tax-percentage-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Default: 12% for freelancers in the Philippines</p>
              </div>
              <div>
                <Label>Withholding Tax</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings.withholding_tax}
                  onChange={(e) => setSettings({ ...settings, withholding_tax: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter withholding tax amount"
                  data-testid="withholding-tax-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Manually enter taxes already withheld by clients</p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={settings.vat_enabled}
                  onCheckedChange={(v) => setSettings({ ...settings, vat_enabled: v })}
                  id="vat"
                  data-testid="vat-toggle"
                />
                <Label htmlFor="vat">Enable VAT</Label>
              </div>
              {settings.vat_enabled && (
                <div>
                  <Label>VAT Percentage (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={settings.vat_percentage}
                    onChange={(e) => setSettings({ ...settings, vat_percentage: parseFloat(e.target.value) })}
                    data-testid="vat-percentage-input"
                  />
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-tax-settings-btn">Save Settings</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ REPORTS TAB ============
const ReportsTab = ({ clients }) => {
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const getExportUrl = (type) => {
    const params = {};
    if (dateRange.start) params.start_date = dateRange.start;
    if (dateRange.end) params.end_date = dateRange.end;
    if (selectedClient) params.client_id = selectedClient;
    if (selectedCategory && type === "expenses") params.category = selectedCategory;

    switch (type) {
      case "income":
        return exportAPI.financialIncome(params);
      case "expenses":
        return exportAPI.financialExpenses(params);
      default:
        return "#";
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" /> Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                data-testid="report-start-date"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                data-testid="report-end-date"
              />
            </div>
            <div>
              <Label>Client</Label>
              <Select value={selectedClient || "all"} onValueChange={(v) => setSelectedClient(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-client">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expense Category</Label>
              <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {EXPENSE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Income Report
            </CardTitle>
            <CardDescription>All income records with applied filters</CardDescription>
          </CardHeader>
          <CardContent>
            <a href={getExportUrl("income")} target="_blank" rel="noopener noreferrer">
              <Button className="w-full" data-testid="download-income-report">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Export to CSV
              </Button>
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-red-600" />
              Expense Report
            </CardTitle>
            <CardDescription>All expenses with applied filters</CardDescription>
          </CardHeader>
          <CardContent>
            <a href={getExportUrl("expenses")} target="_blank" rel="noopener noreferrer">
              <Button className="w-full" data-testid="download-expense-report">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Export to CSV
              </Button>
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Assets Report
            </CardTitle>
            <CardDescription>Complete asset inventory with depreciation</CardDescription>
          </CardHeader>
          <CardContent>
            <a href={exportAPI.financialAssets()} target="_blank" rel="noopener noreferrer">
              <Button className="w-full" data-testid="download-assets-report">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Export to CSV
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>

      {/* BIR Export Pack */}
      <Card className="border-primary">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            BIR Export Pack
          </CardTitle>
          <CardDescription>
            Download all financial documents needed for BIR filing and accountant handoff
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">Included Documents:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Income CSV (filtered by date range)</li>
                <li>• Expense CSV (filtered by date range)</li>
                <li>• Assets CSV (complete list)</li>
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                Export for Accountant / BIR Filing
              </p>
              <div className="flex gap-2">
                <a href={getExportUrl("income")} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" data-testid="bir-income-btn">Income</Button>
                </a>
                <a href={getExportUrl("expenses")} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" data-testid="bir-expenses-btn">Expenses</Button>
                </a>
                <a href={exportAPI.financialAssets()} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" data-testid="bir-assets-btn">Assets</Button>
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ MAIN FINANCIALS PAGE ============
export default function FinancialsPage() {
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("income");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, clientsRes] = await Promise.all([
          financialsAPI.getDashboard(),
          clientsAPI.list(),
        ]);
        setDashboard(dashRes.data);
        setClients(clientsRes.data);
      } catch (err) {
        toast.error("Failed to load financial data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financials-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold">Financials</h1>
        <p className="text-muted-foreground text-sm">Track income, expenses, and prepare for BIR filing</p>
      </div>

      {/* Dashboard Widgets */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card className="overflow-hidden">
            <CardContent className="pt-4">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                <TrendingUp className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Income (This Month)</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold text-green-600 truncate">
                {formatCurrency(dashboard.this_month?.income)}
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="pt-4">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                <TrendingDown className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Expenses (This Month)</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold text-red-600 truncate">
                {formatCurrency(dashboard.this_month?.expenses)}
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="pt-4">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                <DollarSign className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Net Profit</span>
              </div>
              <div className={`text-lg sm:text-2xl font-bold truncate ${(dashboard.this_month?.net_profit || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatCurrency(dashboard.this_month?.net_profit)}
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="pt-4">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                <Calculator className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Est. Tax Payable</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold text-orange-600 truncate">
                {formatCurrency(dashboard.this_month?.estimated_tax)}
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden col-span-2 sm:col-span-1">
            <CardContent className="pt-4">
              <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">Outstanding Invoices</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold text-yellow-600 truncate">
                {formatCurrency(dashboard.outstanding_invoices)}
              </div>
              <div className="text-xs text-muted-foreground">{dashboard.outstanding_count} invoices</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile: horizontally scrollable tabs; desktop: inline */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-max min-w-full sm:w-full sm:grid sm:grid-cols-5">
            <TabsTrigger value="income" className="flex-shrink-0" data-testid="tab-income">
              <TrendingUp className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Income</span>
              <span className="sm:hidden ml-1.5">Income</span>
            </TabsTrigger>
            <TabsTrigger value="expenses" className="flex-shrink-0" data-testid="tab-expenses">
              <TrendingDown className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Expenses</span>
              <span className="sm:hidden ml-1.5">Expenses</span>
            </TabsTrigger>
            <TabsTrigger value="assets" className="flex-shrink-0" data-testid="tab-assets">
              <Package className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Assets</span>
              <span className="sm:hidden ml-1.5">Assets</span>
            </TabsTrigger>
            <TabsTrigger value="tax" className="flex-shrink-0" data-testid="tab-tax">
              <Calculator className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Tax Summary</span>
              <span className="sm:hidden ml-1.5">Tax</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex-shrink-0" data-testid="tab-reports">
              <FileText className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Reports</span>
              <span className="sm:hidden ml-1.5">Reports</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="income" className="mt-6">
          <IncomeTab clients={clients} />
        </TabsContent>
        <TabsContent value="expenses" className="mt-6">
          <ExpensesTab />
        </TabsContent>
        <TabsContent value="assets" className="mt-6">
          <AssetsTab />
        </TabsContent>
        <TabsContent value="tax" className="mt-6">
          <TaxSummaryTab />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <ReportsTab clients={clients} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
